import { useMemo } from 'react';

import {
  add,
  compareAsc,
  differenceInHours,
  differenceInSeconds,
  set
} from 'date-fns';

import { Event } from '../types';
import { ZoomLevel } from '../TimelineView/TimelineBar';
import { ScheduleResponse } from 'generated/index';
import { getIntervalLengthInHours } from '../TimelineView/utils';

function roundToNearest30(date = new Date()) {
  const minutes = 30;
  const ms = 1000 * 60 * minutes;
  return new Date(Math.round(date.getTime() / ms) * ms);
}

export interface Interval {
  start: Date;
  end: Date;
  durationInSeconds: number;
  timeWindow?: Date[];
  percentile: number;
}

function calculateTimeWindows(
  start: Date,
  end: Date,
  zoomLevel: ZoomLevel
): Date[] {
  const durationInHours = differenceInHours(end, start) || 0;
  const intervalLengthInHours = getIntervalLengthInHours(zoomLevel);
  const offsetHours = (24 - start.getHours()) % intervalLengthInHours;
  return [
    ...Array(Math.round(durationInHours / intervalLengthInHours)).keys()
  ].map(
    (_, i) =>
      add(start, {
        minutes: 60 * intervalLengthInHours * i + 60 * offsetHours
      }) ?? []
  );
}

const useTimelineInterval = (
  events: (Event | ScheduleResponse)[],
  zoomLevel: number
): Interval =>
  useMemo(() => {
    if (!events)
      return {
        start: set(new Date(), { hours: 7, minutes: 0 }),
        end: set(new Date(), { hours: 24, minutes: 0 }),
        durationInSeconds: 0,
        percentile: 0
      };

    const dates = [
      ...events.flatMap((o) => (o.start ? new Date(o.start) : [])),
      ...events.flatMap((o) => {
        return o.end ? new Date(o.end) : [];
      })
    ];

    const datesAsc = dates.sort(compareAsc);

    const start = datesAsc[0]
      ? add(roundToNearest30(datesAsc[0]), { hours: -1 })
      : set(new Date(), { hours: 7, minutes: 0 });
    const end = datesAsc[datesAsc.length - 1]
      ? add(roundToNearest30(datesAsc[datesAsc.length - 1]), { hours: 1 })
      : set(new Date(), { hours: 24, minutes: 0 });
    const durationInSeconds = differenceInSeconds(end, start) ?? 0;
    const timeWindow = calculateTimeWindows(start, end, zoomLevel);

    return {
      start,
      end,
      timeWindow,
      durationInSeconds,
      percentile: durationInSeconds / 100
    };
  }, [events, zoomLevel]);

export default useTimelineInterval;
